import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  State,
  process,
} from '@progress/kendo-data-query';
import { TFunction } from 'i18next';
import { FC, ReactNode, createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MESSAGE_MODE, MessageMode } from 'src/constants/messageMode';
import { MESSAGE_STATUS } from 'src/constants/messageStatus';
import useAuth from 'src/hooks/useAuth';
import useBoolean from 'src/hooks/useBoolean';
import useCompany from 'src/hooks/useCompany';
import usePermission from 'src/hooks/usePermissions';
import useThrottleCallback from 'src/hooks/useThrottleCallback';
import { ClientResponse, useGetClientsQuery } from 'src/services/company/useGetClientsQuery';
import useGetMessagesGeneric, {
  Message,
  MessageBoxType,
} from 'src/services/messages_services/useGetMessagesGeneric';
import { Company } from 'src/types/Companies';
import { SetState } from 'src/types/generic';

// Message status constants
const MessageStatus = {
  SENT: '1',
  READ: '2',
  ARCHIVED: '3',
  OLD_DELETED_BY_RECIP: '4',
} as const;

/**
 * Transform filters to use creationDateOnly for date filtering on creationDate field
 * This allows date filters to work on date-only comparisons while sorting still uses full datetime
 */
const transformDateFilters = (filter: CompositeFilterDescriptor): CompositeFilterDescriptor => {
  if (!filter || !filter.filters || filter.filters.length === 0) {
    return filter;
  }

  return {
    ...filter,
    filters: filter.filters.map((f) => {
      // Recursively handle nested composite filters
      if ('filters' in f && f.filters) {
        return transformDateFilters(f as CompositeFilterDescriptor);
      }

      // Transform single filter descriptor
      const filterDesc = f as FilterDescriptor;
      if (filterDesc.field === 'creationDate') {
        return { ...filterDesc, field: 'creationDateOnly' };
      }
      return filterDesc;
    }),
  };
};

// Re-export MessageMode from constants for backward compatibility
export type { MessageMode } from 'src/constants/messageMode';

// Initial states for different views
export const initialInboxMessageState: State = {
  skip: 0,
  take: 50,
  sort: [{ field: 'creationDate', dir: 'desc' }],
  filter: {
    logic: 'and',
    filters: [],
  },
};

export const initialOutboxMessageState: State = {
  skip: 0,
  take: 50,
  sort: [{ field: 'creationDate', dir: 'desc' }],
  filter: {
    logic: 'and',
    filters: [],
  },
};

export const initialGenericMessageState: State = {
  skip: 0,
  take: 50,
  sort: [{ field: 'creationDate', dir: 'desc' }],
  filter: {
    logic: 'and',
    filters: [],
  },
};

// Unified message interface that handles all message types
export interface SelectedMessage {
  messageId: number;
  subject: string;
  message: string;
  status: string;
  decodedStatus: string;
  fromUser?: string;
  sender?: string;
  recipient?: string;
  priority: string | number;
  creationDate?: string | Date | null;
  rowId?: string;
  selected?: boolean;
  isDeleted?: boolean;
  isNewMessage?: boolean;
  createdByUserName?: string;
  isRead?: boolean;
  isRecipientDeleted?: boolean;
  isSent?: boolean;
}

export interface ViewMessageContextType {
  // Mode management (following app pattern)
  mode: MessageMode;
  setMode: SetState<MessageMode>;

  // Messages data (unified for all modes) - readonly since useAsync manages internally
  messages: Message[];
  messageList: SelectedMessage[];
  selectedMessage: SelectedMessage;
  setSelectedMessage: SetState<SelectedMessage>;

  // Data states for different modes (following checkbook pattern)
  inboxDataState: State;
  outboxDataState: State;
  genericDataState: State;
  setInboxDataState: SetState<State>;
  setOutboxDataState: SetState<State>;
  setGenericDataState: SetState<State>;

  // Current mode data state (computed property)
  currentDataState: State;
  setCurrentDataState: React.Dispatch<React.SetStateAction<State>>;

  // Backward compatibility aliases for legacy components
  messagesDataState: State;
  setMessagesDataState: React.Dispatch<React.SetStateAction<State>>;

  // Common props
  clients: ClientResponse[];
  selectedCompany: Company;
  hasEditMessagePermission: boolean;
  isMessageLoading: boolean;
  refreshTable: () => void;
  confirmBeforeProceeding: (acceptCallback: VoidFunction) => void;
  isEditMode: boolean;
  setIsEditMode: SetState<boolean>;
  messageWithFilters: Message[];
  messageTotalWithFilters: number;
  t: TFunction<'translation', undefined>;
  messagesSearch: string;
  setMessagesSearch: SetState<string>;
  pageTitle: string;
  filteredMessages: Message[];

  unreadCount: number;
  sentCount: number;

  // Current client ID from dropdown selection
  currentClientId: string;

  // Mode-specific methods (useAsync execute functions)
  getInboxMessages: () => Promise<Message[]>;
  getOutboxMessages: () => Promise<Message[]>;
  getGenericMessages: () => Promise<Message[]>;
}

export const ViewMessageContext = createContext<ViewMessageContextType>(
  {} as ViewMessageContextType
);

interface ViewMessageContextProps {
  children: ReactNode;
  initialMode?: MessageMode;
}

const ViewMessageContextProvider: FC<ViewMessageContextProps> = ({
  children,
  initialMode = MESSAGE_MODE.GENERIC,
}) => {
  const { company: selectedCompany } = useCompany();
  const { t } = useTranslation();
  const { companyId } = selectedCompany;
  const { clientId: clientIdFromToken } = useAuth();
  const hasEditMessagePermission = usePermission({ permissions: [] });

  // Mode management
  const [mode, setMode] = useState<MessageMode>(initialMode);

  // Get clientId from JWT token (set by AuthContext from decoded token)
  const [currentClientId, setCurrentClientId] = useState<string>(() => {
    const tokenClientId = clientIdFromToken || '';

    return tokenClientId || companyId || '';
  });

  // Separate data states for each mode (following checkbook pattern)
  const [inboxDataState, setInboxDataState] = useState<State>(initialInboxMessageState);
  const [outboxDataState, setOutboxDataState] = useState<State>(initialOutboxMessageState);
  const [genericDataState, setGenericDataState] = useState<State>(initialGenericMessageState);

  // API hook for messages (consolidated from 3 separate hooks)
  // Always use clientId from JWT token for API calls
  // Memoize to prevent recreating API hooks when fallback values change but result is same
  const apiClientId = useMemo(
    () => clientIdFromToken || companyId || '',
    [clientIdFromToken, companyId]
  );

  // Compute boxType based on current mode
  const boxType: MessageBoxType = useMemo(
    () =>
      // Generic and Outbox both use 'outbox' endpoint
      mode === MESSAGE_MODE.INBOX ? 'inbox' : 'outbox',
    [mode]
  );

  // Single API hook - only creates the hook for the currently active mode
  const { messages, getMessages, isMessagesLoading } = useGetMessagesGeneric({
    clientId: apiClientId,
    boxType,
    sortColumn: mode === MESSAGE_MODE.INBOX ? 'creationDate' : 'subject',
  });

  const { data: clients = [] } = useGetClientsQuery(companyId);

  const { value: isEditMode, setBool: setIsEditMode } = useBoolean(false);
  const [selectedMessage, setSelectedMessage] = useState<SelectedMessage>({} as SelectedMessage);
  const [messagesSearch, setMessagesSearch] = useState('');

  // Migration: Convert any old 'creationDateOnly' sort fields to 'creationDate'
  useEffect(() => {
    const migrateSortField = (state: State): State => {
      if (state.sort && state.sort.length > 0) {
        const needsMigration = state.sort.some((s) => s.field === 'creationDateOnly');
        if (needsMigration) {
          return {
            ...state,
            sort: state.sort.map((s) =>
              s.field === 'creationDateOnly' ? { ...s, field: 'creationDate' } : s
            ),
          };
        }
      }
      return state;
    };

    setInboxDataState((prevState) => migrateSortField(prevState));
    setOutboxDataState((prevState) => migrateSortField(prevState));
    setGenericDataState((prevState) => migrateSortField(prevState));
  }, []); // Run once on mount

  // Note: messages and isMessagesLoading come directly from the single API hook
  // No need for computed properties - the hook is already mode-specific via boxType parameter
  const isMessageLoading = isMessagesLoading;

  const currentDataState = useMemo(() => {
    switch (mode) {
      case MESSAGE_MODE.INBOX:
        return inboxDataState;
      case MESSAGE_MODE.OUTBOX:
        return outboxDataState;
      case MESSAGE_MODE.GENERIC:
      default:
        return genericDataState;
    }
  }, [mode, inboxDataState, outboxDataState, genericDataState]);

  const setCurrentDataState = useCallback(
    (newState: React.SetStateAction<State>) => {
      switch (mode) {
        case MESSAGE_MODE.INBOX:
          setInboxDataState(newState);
          break;
        case MESSAGE_MODE.OUTBOX:
          setOutboxDataState(newState);
          break;
        case MESSAGE_MODE.GENERIC:
        default:
          setGenericDataState(newState);
          break;
      }
    },
    [mode]
  );

  //Process messages into messageList with mode-specific logic
  //Exclude "deleted by recipient" (status '4') which is for messages prior to 2025.
  //Messages from 2025 and forward rely ONLY on isDeleted flags to determine "deleted by recipient"
  const messageList: SelectedMessage[] = useMemo(() => {
    const filteredMessages =
      mode === MESSAGE_MODE.INBOX
        ? messages.filter((msg) => String(msg.status) !== MessageStatus.OLD_DELETED_BY_RECIP)
        : messages;

    return filteredMessages.map((item) => ({
      messageId: item.messageId,
      subject: item.subject ?? '',
      message: item.message ?? '',
      status: String(item.status ?? ''),
      decodedStatus: item.isDeleted ? 'Deleted by all' : item.decodedStatus ?? '',
      sender: mode === MESSAGE_MODE.INBOX ? item.createdByUserName ?? '' : undefined,
      recipient: mode === MESSAGE_MODE.OUTBOX ? item.recipient ?? '' : undefined,
      fromUser: item.createdByUserName ?? '',
      priority: item.priority ?? '',
      decodedPriority: item.decodedPriority ?? '',
      creationDate: item.creationDate ?? '',
      rowId: item.rowId,
      selected: item.selected,
      isDeleted: item.isDeleted,
      isRecipientDeleted:
        mode === MESSAGE_MODE.INBOX
          ? item.decodedStatus.toLowerCase() === MESSAGE_STATUS.DELETED_BY_RECIPIENT
          : false,
      isNewMessage: item.isNewMessage,
      createdByUserName: item.createdByUserName ?? '',
      isRead:
        mode === MESSAGE_MODE.INBOX
          ? item.status.toLowerCase() === MESSAGE_STATUS.READ ||
            item.decodedStatus.toLowerCase() === MESSAGE_STATUS.READ
          : undefined,
      isSent:
        mode === MESSAGE_MODE.OUTBOX
          ? item.status.toLowerCase() === 'sent' || item.decodedStatus.toLowerCase() === 'sent'
          : undefined,
    }));
  }, [messages, mode]);

  // Filter & process messages for grid
  const { data: messageWithFilters, total: messageTotalWithFilters } = useMemo(() => {
    const filteredData =
      mode === MESSAGE_MODE.OUTBOX ? [...messages] : messages.filter((m) => !m.isNewMessage);

    if (filteredData.length === 0) {
      return { data: [], total: 0 };
    }

    if (mode === MESSAGE_MODE.INBOX) {
      // Apply filtering only (no sorting or pagination) - let Grid handle those
      // Transform date filters to use creationDateOnly for date-only comparison
      const processDataState = {
        skip: undefined,
        take: undefined,
        sort: undefined,
        filter: currentDataState.filter ? transformDateFilters(currentDataState.filter) : undefined,
      };

      const result = process(filteredData, processDataState);
      return {
        data: result.data,
        total: result.total,
      };
    } else {
      //Use process for outbox & generic - apply filtering only

      if (filteredData.length === 0) {
        return { data: [], total: 0 };
      }

      // Apply filtering only (no sorting or pagination) - let Grid handle those
      // Transform date filters to use creationDateOnly for date-only comparison
      const processDataState = {
        skip: undefined,
        take: undefined,
        sort: undefined,
        filter: currentDataState.filter ? transformDateFilters(currentDataState.filter) : undefined,
      };

      const result = process(filteredData, processDataState);
      return result as { data: typeof filteredData; total: number };
    }
  }, [messages, currentDataState, mode]);

  // Internal refresh logic without throttling
  const refreshTableInternal = useCallback(() => {
    if (mode === MESSAGE_MODE.OUTBOX) {
      setOutboxDataState(initialOutboxMessageState);
    }
    getMessages();
  }, [mode, getMessages]);

  // Apply throttle: minimum 2 seconds between refresh calls to prevent rapid API requests
  const refreshTable = useThrottleCallback(refreshTableInternal, 2000);

  const confirmBeforeProceeding = useCallback((acceptCallback: VoidFunction) => {
    acceptCallback();
  }, []);

  const unreadCount = useMemo(() => {
    if (mode !== 'inbox') return 0;
    return messageList.filter((msg) => !msg.isRead && !msg.isDeleted && !msg.isRecipientDeleted)
      .length;
  }, [messageList, mode]);

  const sentCount = useMemo(() => {
    if (mode !== 'outbox') return 0;
    return messageList.filter((msg) => msg.isSent && !msg.isDeleted).length;
  }, [messageList, mode]);

  //Load messages when JWT clientId or mode changes
  useEffect(() => {
    if (apiClientId && apiClientId !== '') {
      // useAsync API handles state management internally, just call execute function
      // Always uses clientId from JWT token
      // The hook automatically fetches for the correct mode via boxType parameter
      getMessages().catch((error) => {
        console.error(`ViewMessageContext - getMessages (${mode}) failed:`, error);
      });
    } else {
      console.log('ViewMessageContext - Not calling API, no valid clientId from JWT token');
    }
    // Note: useAsync API handles clearing data when parameters change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiClientId, mode]);

  //Listen for clientId changes from ViewMessageNavbar (for UI state only, not API calls)
  useEffect(() => {
    const handleClientIdChange = (event: CustomEvent) => {
      const newClientId = event.detail.clientId;
      // Note: This updates UI state only. API calls always use JWT token clientId
      setCurrentClientId(newClientId);

      //Reset pagination when switch to different client, but keep default sort
      if (newClientId !== currentClientId) {
        switch (mode) {
          case 'inbox':
            setInboxDataState((prevState) => ({
              ...prevState,
              sort: [{ field: 'creationDate', dir: 'desc' }],
              skip: 0,
            }));
            break;
          case 'outbox':
            setOutboxDataState((prevState) => ({
              ...prevState,
              sort: [{ field: 'creationDate', dir: 'desc' }],
              skip: 0,
            }));
            break;
          case 'generic':
          default:
            setGenericDataState((prevState) => ({
              ...prevState,
              sort: [{ field: 'creationDate', dir: 'desc' }],
              skip: 0,
            }));
            break;
        }
      }
    };

    window.addEventListener('clientIdChanged', handleClientIdChange as EventListener);
    return () => {
      window.removeEventListener('clientIdChanged', handleClientIdChange as EventListener);
    };
  }, [currentClientId, mode]);

  const pageTitle = useMemo(() => {
    switch (mode) {
      case 'inbox':
        return t('Messages Inbox');
      case 'outbox':
        return hasEditMessagePermission ? t('Messages Outbox') : t('View Messages');
      case 'generic':
      default:
        return hasEditMessagePermission ? t('Messages Outbox') : t('View Messages');
    }
  }, [mode, hasEditMessagePermission, t]);

  //Define filteredMessages using mode-specific logic
  const filteredMessages = useMemo(() => {
    // Sort messages: new messages first, then old messages
    const newMessages = [];
    const oldMessages = [];
    for (const msg of messages) {
      if (msg.isNewMessage) {
        newMessages.push(msg);
      } else {
        oldMessages.push(msg);
      }
    }
    return [...newMessages, ...oldMessages];
  }, [messages]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      mode,
      setMode,
      messages,
      messageList,
      selectedMessage,
      setSelectedMessage,
      inboxDataState,
      outboxDataState,
      genericDataState,
      setInboxDataState,
      setOutboxDataState,
      setGenericDataState,
      currentDataState,
      setCurrentDataState,
      // Backward compatibility aliases
      messagesDataState: currentDataState,
      setMessagesDataState: setCurrentDataState,
      clients,
      selectedCompany,
      hasEditMessagePermission,
      isMessageLoading,
      refreshTable,
      confirmBeforeProceeding,
      isEditMode,
      setIsEditMode,
      filteredMessages,
      messageWithFilters,
      messageTotalWithFilters,
      t,
      messagesSearch,
      setMessagesSearch,
      pageTitle,
      unreadCount,
      sentCount,
      currentClientId,
      // Backward compatibility: expose same method names that call the unified hook
      getInboxMessages: getMessages,
      getOutboxMessages: getMessages,
      getGenericMessages: getMessages,
    }),
    [
      mode,
      setMode,
      messages,
      messageList,
      selectedMessage,
      inboxDataState,
      outboxDataState,
      genericDataState,
      currentDataState,
      setCurrentDataState,
      clients,
      selectedCompany,
      hasEditMessagePermission,
      isMessageLoading,
      refreshTable,
      confirmBeforeProceeding,
      isEditMode,
      setIsEditMode,
      filteredMessages,
      messageWithFilters,
      messageTotalWithFilters,
      t,
      messagesSearch,
      pageTitle,
      unreadCount,
      sentCount,
      currentClientId,
      getMessages,
    ]
  );

  return <ViewMessageContext.Provider value={contextValue}>{children}</ViewMessageContext.Provider>;
};

export default ViewMessageContextProvider;
